<!--
 * @Description: 
 * @Date: 2022-09-19 11:03:48
-->
<template>
  <div ref="TableDomRef">
    <BaseTable
      size="small"
      ref="TableRef"
      height="500px"
      :data="_data"
      :columns="tableConfig.tableColumns"
      v-bind="tableConfig.baseConfig"
    ></BaseTable>
  </div>
</template>

<script lang="tsx" setup>
import {
  XrkTableProps,
  XrkTableRef,
  BaseTable,
  BaseIcon,
  BaseSwitch,
  BaseToolTip
} from 'xrk-components';
import { dealTableColumnstextLineClamp } from '../../tools/index';
import { TabaleColumnType } from '../../data-source/types';
import { onMounted, ref, watch } from 'vue';
import Sortable from 'sortablejs';
import { Sort } from '@element-plus/icons-vue';
import { cloneDeep } from 'lodash';

type TableConfig = XrkTableProps<{}, TabaleColumnType>;

const props = defineProps<{
  maxColunms: number;
  data: Array<TabaleColumnType>;
}>();

const DragButtonClassName = 'xrk-table--drag__btn';
const DragDisabledClassName = 'xrk-table--drag__disabled';
const { TableRef, TableDomRef } = (() => {
  const TableDomRef = ref<HTMLDivElement>();
  const TableRef = ref<XrkTableRef>();
  return {
    TableRef,
    TableDomRef
  };
})();

const _data = ref<(typeof props)['data']>([]);
const _dataIdSort = [];
const setDataSort = () => {
  _data.value.forEach((row, index) => {
    _dataIdSort[index] = row.id;
  });
};
const setSelectionRows = () => {
  _data.value.forEach(row => {
    if (row.isShow) {
      TableRef.value?.addSelectionRow(row.id, row);
    } else {
      TableRef.value?.removeSelectionRow(row.id);
    }
  });
};

watch(
  () => props.data,
  val => {
    _data.value = cloneDeep(val);
    setDataSort();
    setSelectionRows();
  },
  {
    deep: true,
    immediate: true
  }
);

const tableConfig: TableConfig['tableConfig'] = {
  baseConfig: {
    rowClassName: ({ row }) => (row.disabled ? DragDisabledClassName : '')
  },
  tableColumns: dealTableColumnstextLineClamp<
    TableConfig['tableConfig']['tableColumns']
  >([
    {
      prop: 'id',
      type: '_selection',
      renderHeader: () => <div>显示</div>,
      selectionDisabled: row => {
        if (row.disabled) return true;
        const allSelectionRows = TableRef.value?.getSelectionRows();
        if (allSelectionRows.findIndex(i => row.id === i.id) !== -1)
          return false;
        if (allSelectionRows?.length >= props.maxColunms) return true;
        return false;
      },
      selectionAddBefore: (row, done) => {
        row.isShow = true;
        done();
      },
      selectionRemoveBefore: (row, done) => {
        row.isShow = false;
        row.isSearch = false;
        done();
      },
      width: 80
    },
    { prop: 'label', label: '列名' },
    {
      prop: 'isSearch',
      label: '是否作为搜索条件',
      render: ({ row, cellValue, column }) => {
        return (
          <BaseSwitch
            disabled={!row.isShow}
            modelValue={cellValue}
            onChange={val => {
              row[column.property] = val;
            }}
          ></BaseSwitch>
        );
      }
    },
    {
      prop: 'sort',
      label: '拖动调整顺序',
      className: DragButtonClassName,
      render: ({ row }) => {
        return (
          <BaseToolTip content="拖动排序" disabled={row.disabled}>
            <BaseIcon
              style={row.disabled ? '' : 'cursor:grab'}
              color={row.disabled ? '#666' : 'var(--xrk-color-primary)'}
            >
              <Sort />
            </BaseIcon>
          </BaseToolTip>
        );
      }
    }
  ])
};
const initSortable = () => {
  const getDisabledDragParent = (el: HTMLElement) => {
    if (el.classList.contains(DragDisabledClassName)) {
      return el;
    }
    if (el.parentElement) {
      return getDisabledDragParent(el.parentElement);
    }
    return null;
  };
  const el = TableDomRef.value.querySelector(
    '.xrk-table__body-wrapper tbody'
  ) as HTMLElement;
  if (el) {
    Sortable.create(el, {
      animation: 150,
      scroll: TableDomRef.value.parentElement,
      scrollSensitivity: 30,
      handle: `.${DragButtonClassName}`,
      filter: `.${DragDisabledClassName}`,
      onEnd: ({ oldIndex, newIndex }) => {
        _dataIdSort.splice(newIndex, 0, _dataIdSort.splice(oldIndex, 1)[0]);
      },
      onMove: (_, originalEvent) => {
        const targetDOM = originalEvent.target as HTMLElement;
        return !getDisabledDragParent(targetDOM);
      }
    });
  }
};

onMounted(() => {
  setSelectionRows();
  initSortable();
});

defineExpose({
  getAllData: () => {
    const map = _data.value.reduce((map, row) => {
      map[row.id] = row;
      return map;
    }, {});
    return _dataIdSort.map((id, index) => ({ ...map[id], sort: index + 1 }));
  }
});
</script>

<style></style>
